import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { RecordInvoicePaymentInput } from '@accounting/shared';
import { useInvoice, useIssueInvoice, useRecordInvoicePayment, useRemoveInvoicePayment, useVoidInvoice } from '../../hooks/useInvoices';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { AttachmentsPanel } from '../../components/attachments/AttachmentsPanel';
import { ApiError, openFileInNewTab } from '../../lib/api';
import { formatAccountingDate, formatCurrency, todayIso } from '../../lib/formatters';
import { invoiceStatusTag } from './invoices.index';

export const Route = createFileRoute('/_auth/invoices/$id')({
  component: InvoiceDetailPage,
});

function InvoiceDetailPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: invoice, isLoading, error } = useInvoice(companyId, id);
  const voidInvoice = useVoidInvoice(companyId);
  const issue = useIssueInvoice(companyId);
  const removePayment = useRemoveInvoicePayment(companyId);
  const [payOpen, setPayOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [includeAging, setIncludeAging] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !invoice) return <p className="field-error">Failed to load invoice.</p>;

  const voidError =
    voidInvoice.error instanceof ApiError
      ? (voidInvoice.error.body as { error?: string } | null)?.error ?? 'Void failed'
      : voidInvoice.error?.message;

  const isDraft = invoice.status === 'draft';
  const canPay = invoice.status !== 'void' && !isDraft && parseFloat(invoice.outstandingAmount) > 0;
  const issueError =
    issue.error instanceof ApiError
      ? (issue.error.body as { error?: string } | null)?.error ?? 'Could not issue invoice'
      : issue.error?.message;

  return (
    <div>
      <Link to="/invoices" style={{ fontSize: 13 }}>← Invoices</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>
          Invoice {invoice.invoiceNumber} <span style={{ marginLeft: 8 }}>{invoiceStatusTag(invoice)}</span>
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-secondary" onClick={() => setPrintOpen((v) => !v)}>
            Print / PDF
          </button>
          {isDraft && (
            <button className="btn-primary" disabled={issue.isPending} onClick={() => issue.mutate(invoice.id)}>
              {issue.isPending ? 'Issuing…' : 'Issue invoice'}
            </button>
          )}
          {canPay && (
            <button className="btn-primary" onClick={() => setPayOpen(true)}>Receive payment</button>
          )}
          {invoice.status !== 'void' && (
            <Link to="/invoices/$id/edit" params={{ id: invoice.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
              Edit
            </Link>
          )}
          {invoice.status !== 'void' && (
            <button
              className="btn-danger"
              disabled={voidInvoice.isPending}
              onClick={() => {
                const reason = prompt('Reason for voiding this invoice?');
                if (reason && reason.trim()) voidInvoice.mutate({ id: invoice.id, reason: reason.trim() });
              }}
            >
              Void
            </button>
          )}
        </div>
      </div>
      {voidError && <div className="field-error" style={{ marginBottom: 12 }}>{voidError}</div>}

      {printOpen && (
        <div className="card" style={{ padding: 16, marginBottom: 16, maxWidth: 520 }}>
          <strong style={{ fontSize: 14 }}>Print invoice</strong>
          <p className="muted" style={{ fontSize: 13, margin: '4px 0 8px' }}>
            Opens a printable PDF with the computed payment-due date and a thank-you note.
          </p>
          {invoice.customerId && (
            <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={includeAging} onChange={(e) => setIncludeAging(e.target.checked)} />
              Include account aging summary (Current / Over 30 / 60 / 90)
            </label>
          )}
          {printError && <div className="field-error" style={{ marginTop: 8 }}>{printError}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              className="btn-primary"
              onClick={() => {
                setPrintError(null);
                openFileInNewTab(
                  `/companies/${companyId}/invoices/${invoice.id}/invoice.pdf`,
                  includeAging ? { aging: 'true' } : undefined,
                ).catch((err) =>
                  setPrintError(err instanceof Error ? err.message : 'Could not open the PDF'),
                );
              }}
            >
              Open PDF
            </button>
            <button className="btn-secondary" onClick={() => setPrintOpen(false)}>Close</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 16, maxWidth: 820, marginBottom: 16 }}>
        <div className="form-grid" style={{ marginBottom: 8 }}>
          <span className="muted">Customer</span>
          <strong>{invoice.customerName ?? <span className="muted">(no customer)</span>}</strong>
          <span className="muted">Invoice date</span>
          <span>{formatAccountingDate(invoice.invoiceDate)}</span>
          <span className="muted">Due date</span>
          <span>{formatAccountingDate(invoice.dueDate)}{invoice.isOverdue ? ' (past due)' : ''}</span>
          {invoice.terms && (<><span className="muted">Terms</span><span>{invoice.terms}</span></>)}
          {invoice.memo && (<><span className="muted">Memo</span><span>{invoice.memo}</span></>)}
        </div>

        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Description</th>
              <th style={{ textAlign: 'right' }}>Qty</th>
              <th style={{ textAlign: 'right' }}>Rate</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l) => (
              <tr key={l.id}>
                <td>{l.accountCode} — {l.accountName}</td>
                <td className="muted">{l.description ?? ''}</td>
                <td style={{ textAlign: 'right' }}>{l.quantity}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(l.unitPrice)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
              <td colSpan={4}>Total</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(invoice.total)}</td>
            </tr>
            <tr>
              <td colSpan={4} className="muted">Paid</td>
              <td style={{ textAlign: 'right' }} className="muted">{formatCurrency(invoice.amountPaid)}</td>
            </tr>
            <tr style={{ fontWeight: 700 }}>
              <td colSpan={4}>Balance due</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(invoice.outstandingAmount)}</td>
            </tr>
          </tfoot>
        </table>

        {invoice.journalEntryId && (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            Posted as <Link to="/journal/$id" params={{ id: invoice.journalEntryId }}>journal entry</Link>.
            {invoice.status === 'void' && invoice.voidReason && ` Voided: ${invoice.voidReason}.`}
          </p>
        )}
      </div>

      {invoice.payments.length > 0 && (
        <div className="card" style={{ padding: 16, maxWidth: 820, marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, marginTop: 0 }}>Payments received</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Deposited to</th>
                <th>Reference</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ width: 1 }}></th>
              </tr>
            </thead>
            <tbody>
              {invoice.payments.map((p) => (
                <tr key={p.id}>
                  <td>{formatAccountingDate(p.paymentDate)}</td>
                  <td className="muted">{p.depositAccountCode} — {p.depositAccountName}</td>
                  <td className="muted">{p.reference ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(p.amount)}</td>
                  <td>
                    <button
                      className="btn-danger"
                      disabled={removePayment.isPending}
                      title="Remove this payment — posts a reversing entry and reopens the balance"
                      onClick={() => {
                        if (
                          confirm(
                            `Remove the ${formatCurrency(p.amount)} payment from ${formatAccountingDate(p.paymentDate)}? ` +
                              'A reversing journal entry is posted and the invoice balance reopens.',
                          )
                        ) {
                          removePayment.mutate({ invoiceId: invoice.id, paymentId: p.id });
                        }
                      }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {removePayment.error && (
            <div className="field-error">
              {removePayment.error instanceof ApiError
                ? ((removePayment.error.body as { error?: string } | null)?.error ?? 'Could not remove the payment')
                : removePayment.error.message}
            </div>
          )}
        </div>
      )}

      <AttachmentsPanel companyId={companyId} entityType="invoice" entityId={invoice.id} />

      {payOpen && (
        <ReceivePaymentModal
          companyId={companyId}
          invoiceId={invoice.id}
          outstanding={invoice.outstandingAmount}
          onClose={() => setPayOpen(false)}
        />
      )}
    </div>
  );
}

function ReceivePaymentModal({
  companyId,
  invoiceId,
  outstanding,
  onClose,
}: {
  companyId: string;
  invoiceId: string;
  outstanding: string;
  onClose: () => void;
}) {
  const pay = useRecordInvoicePayment(companyId);
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [amount, setAmount] = useState(outstanding);
  const [reference, setReference] = useState('');

  const errorMessage =
    pay.error instanceof ApiError
      ? (pay.error.body as { error?: string } | null)?.error ?? 'Payment failed'
      : pay.error?.message;

  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4, marginTop: 10 } as const;

  const submit = () => {
    const input: RecordInvoicePaymentInput = {
      paymentDate,
      amount: String(parseFloat(amount) || 0),
      reference: reference.trim() || undefined,
    };
    pay.mutate({ id: invoiceId, input }, { onSuccess: () => onClose() });
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, marginTop: 0 }}>Receive payment</h2>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Posts to Undeposited Funds (debit) against Accounts Receivable (credit). Move it to a bank
          later with a deposit. Balance due: {formatCurrency(outstanding)}.
        </p>

        <label style={labelStyle} htmlFor="p-date">Payment date</label>
        <input id="p-date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} style={{ width: '100%' }} />

        <label style={labelStyle} htmlFor="p-amt">Amount</label>
        <input id="p-amt" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} style={{ width: '100%', textAlign: 'right' }} />

        <label style={labelStyle} htmlFor="p-ref">Reference</label>
        <input id="p-ref" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="(optional)" style={{ width: '100%' }} />

        {errorMessage && <div className="field-error" style={{ marginTop: 12 }}>{errorMessage}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={pay.isPending || !(parseFloat(amount) > 0)}>
            {pay.isPending ? 'Posting…' : 'Record payment'}
          </button>
        </div>
      </div>
    </div>
  );
}
